class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.requestController = null;
    this.focusElement = this.input;
    this.resetButton.addEventListener('click', this.clear.bind(this));
    this.input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 250));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');
    this.toggleNavigationButton = document.querySelector('.toggle-navigation-button');
    this.pageScrollLocked = false;
    this.pageScrollY = 0;
    this.boundUpdateViewportMetrics = this.updateViewportMetrics.bind(this);
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));
    this.isHeaderSearch = this.closest('header') !== null;
    document.addEventListener('menu-drawer:open', () => this.toggleSearchState(false));
    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }

  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  onFocus() {
    this.updateViewportMetrics();
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      this.setHeaderSearchState(true);
    }
    if (!this.searchRecommendationEmpty) this.searchContent.classList.remove('hidden');
    this.classList.add(this.states.OPEN);
    if (this.getQuery().length === 0) {
      if (this.searchRecommendationEmpty) this.searchContent.classList.add('hidden');
      return;
    }
    this.renderSection(this.setupURL().toString());
  }

  getQuery() {
    return this.input.value.trim();
  }

  clear(event = null) {
    event?.preventDefault();
    this.requestController?.abort();
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.removeAttribute('error');
    this.toggleSearchState(false);
  }

  handleProductTypeChange() {
    if (this.getQuery().length > 0) this.renderSection(this.setupURL().toString());
  }

  setupURL() {
    const rootUrl = FoxTheme.routes.root_url || '/';
    const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
    const url = new URL(`${normalizedRoot}search`, window.location.origin);
    let searchTerm = this.getQuery();
    if (this.searchProductTypes?.value) {
      searchTerm = `product_type:${this.searchProductTypes.value} AND ${searchTerm}`;
    }
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'last');
    url.searchParams.set('sort_by', 'relevance');
    url.searchParams.set('section_id', 'tejjari-search-suggestions');
    return url;
  }

  onChange() {
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    this.renderSection(this.setupURL().toString());
  }

  renderSection(url) {
    if (this.cachedMap.has(url)) {
      this.renderSearchResults(this.cachedMap.get(url));
      return;
    }
    this.renderSectionFromFetch(url);
  }

  renderSectionFromFetch(url) {
    this.requestController?.abort();
    const controller = new AbortController();
    this.requestController = controller;
    this.setLoadingState(true);
    fetch(url, {
      credentials: 'same-origin',
      headers: { Accept: 'text/html' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Search request failed with status ${response.status}`);
        return response.text();
      })
      .then((responseText) => {
        if (controller.signal.aborted) return;
        this.cachedMap.set(url, responseText);
        this.renderSearchResults(responseText);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        if (this.requestController === controller) this.setLoadingState(false);
      });
  }

  renderSearchResults(responseText) {
    const targetId = `PredictiveSearchResults-${FoxTheme.utils.getSectionId(this)}`;
    const targetElement = document.getElementById(targetId);
    const parsedDoc = new DOMParser().parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById('TejjariSearchSuggestions');
    if (!targetElement || !contentElement) {
      this.setAttribute('error', 'Invalid search response');
      return;
    }
    targetElement.innerHTML = contentElement.innerHTML;
    this.removeAttribute('error');
    this.setAttribute('results', 'true');
    this.input.setAttribute('aria-expanded', 'true');
    this.searchContent?.classList.remove('hidden');
    if (this.isHeaderSearch) {
      this.setHeaderSearchState(true);
    }
    this.classList.add(this.states.OPEN);
  }

  handleClickOutside(event) {
    const target = event.target;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.classList.contains('header__search-close'))
      : !this.contains(target);
    if (shouldClose) setTimeout(() => this.toggleSearchState(false));
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    this.input.setAttribute('aria-expanded', String(isOpen));
    if (this.isHeaderSearch) this.setHeaderSearchState(isOpen);
    if (!isOpen && this.searchRecommendationEmpty) this.searchContent?.classList.add('hidden');
  }

  updateViewportMetrics() {
    const visualViewport = window.visualViewport;
    const viewportBottom = visualViewport
      ? visualViewport.height + visualViewport.offsetTop
      : window.innerHeight;
    const headerBottom = this.header?.getBoundingClientRect().bottom || 0;

    document.documentElement.style.setProperty('--viewport-height', `${Math.round(viewportBottom)}px`);
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${Math.max(0, Math.round(headerBottom))}px`
    );
  }

  setHeaderSearchState(isOpen) {
    if (isOpen && !this.pageScrollLocked) {
      this.pageScrollY = window.scrollY;
      document.documentElement.style.setProperty('--search-scroll-offset', `${-this.pageScrollY}px`);
      window.addEventListener('resize', this.boundUpdateViewportMetrics);
      window.visualViewport?.addEventListener('resize', this.boundUpdateViewportMetrics);
      window.visualViewport?.addEventListener('scroll', this.boundUpdateViewportMetrics);
      this.pageScrollLocked = true;
    }

    document.documentElement.classList.toggle(this.states.SEARCH_OPEN, isOpen);
    document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
    this.toggleNavButtonVisibility(!isOpen);

    if (isOpen) {
      this.updateViewportMetrics();
      return;
    }

    if (this.pageScrollLocked) {
      window.removeEventListener('resize', this.boundUpdateViewportMetrics);
      window.visualViewport?.removeEventListener('resize', this.boundUpdateViewportMetrics);
      window.visualViewport?.removeEventListener('scroll', this.boundUpdateViewportMetrics);
      document.documentElement.style.removeProperty('--search-scroll-offset');
      this.pageScrollLocked = false;
      window.scrollTo(0, this.pageScrollY);
    }
  }

  disconnectedCallback() {
    this.requestController?.abort();
    document.removeEventListener('click', this.boundHandleClickOutside);
    if (this.pageScrollLocked) this.setHeaderSearchState(false);
  }

  toggleNavButtonVisibility(isOpen) {
    this.toggleNavigationButton?.classList.toggle('is-show', isOpen);
  }

  setLoadingState(isLoading) {
    this.toggleAttribute('loading', isLoading);
    this.resetButton.classList.toggle(this.states.LOADING, isLoading);
  }
}

customElements.define('predictive-search', PredictiveSearch);
